import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, Directive, inject, input, OnInit, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MfeCoreModule } from '@mynexus/mfe-core';
import { MessageService } from 'primeng/api';
import { ButtonDirective, ButtonModule } from 'primeng/button';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { CareCoordinationService } from '@core/service/care-coordination.service';
import { ReferralFormSubmission } from '@feature/member-detail/models/care-coordination-sub-widget.model';

export function getReferralFileName(subscriberNumber: string, dateOfReferral: string): string {
  const utcDate = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateOfReferral) ? dateOfReferral : `${dateOfReferral}Z`;

  return `Case_Management_${subscriberNumber}_${formatDate(utcDate, 'MM.dd.yyyy', 'en-US')}.pdf`;
}

@Directive({
  selector: '[downloadReferral]',
  host: {
    '(click)': 'download()',
    '[attr.aria-busy]': 'isDownloading()',
  },
})
export class DownloadReferralDirective {
  readonly referralId = input.required<string>({ alias: 'downloadReferral' });
  readonly tenantId = input.required<string>();
  readonly memberId = input.required<string>();
  readonly subscriberNumber = input.required<string>();
  readonly dateOfReferral = input.required<string>();

  readonly isDownloading = signal(false);

  private readonly careCoordinationService = inject(CareCoordinationService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pButton = inject(ButtonDirective, { optional: true });

  download(): void {
    if (this.isDownloading()) {
      return;
    }

    this.setLoading(true);

    this.careCoordinationService
      .downloadCaseManagementReferral(this.tenantId(), this.memberId(), this.referralId())
      .pipe(
        finalize(() => this.setLoading(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => this.saveFile(response.body, getReferralFileName(this.subscriberNumber(), this.dateOfReferral())),
        error: () =>
          this.messageService.add({
            key: 'httpActionKey',
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to download referral. Please try again.',
            life: 5000,
            closable: true,
          }),
      });
  }

  private setLoading(value: boolean): void {
    this.isDownloading.set(value);

    if (this.pButton) {
      this.pButton.loading = value;
    }
  }

  private saveFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export interface ViewReferralDialogData {
  tenantId: string;
  memberId: string;
  subscriberNumber: string;
  referralId: string;
  dateOfReferral: string;
}

@Component({
  selector: 'app-view-referral-dialog',
  templateUrl: './view-referral-dialog.component.html',
  imports: [MfeCoreModule, ButtonModule, DownloadReferralDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewReferralDialogComponent implements OnInit {
  readonly data: ViewReferralDialogData = inject(DynamicDialogConfig).data;
  readonly isLoading = signal(true);
  readonly formSubmission = signal<ReferralFormSubmission | null>(null);

  private readonly careCoordinationService = inject(CareCoordinationService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.careCoordinationService
      .getCaseManagementReferralSubmission(this.data.tenantId, this.data.memberId, this.data.referralId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ form, submission }) =>
          this.formSubmission.set({ form: { ...form, components: form.components.filter(component => component.type !== 'button') }, submission }),
        error: () => this.formSubmission.set(null),
      });
  }
}
